import {
  Box,
  Button,
  Flex,
  FlexProps,
  Popover,
  Text,
  TextField,
} from '@radix-ui/themes';
import { Country, ORDERED_COUNTRIES } from 'classes/helpers/phone.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { forwardRef, memo, PropsWithChildren, useMemo, useState } from 'react';
import { Virtuoso, VirtuosoProps } from 'react-virtuoso';
import './country-picker.scss';

const LIST_HEIGHT = '17.5rem';

interface IProps {
  country: Country;
  disabled?: boolean;
  onChange: (country: Country) => void;
}

// matches on country name or the digits of its calling code
const matchesQuery = (c: Country, q: string): boolean => {
  const name = q.toLowerCase();
  const digits = q.replace(/\D/g, '');

  return (
    c.name.toLowerCase().includes(name) ||
    (digits.length > 0 && c.callingCode.startsWith(digits))
  );
};

const CountryPickerList = forwardRef(
  (
    { children, style }: PropsWithChildren<FlexProps>,
    ref: React.Ref<HTMLDivElement>
  ) => {
    return (
      <Flex direction="column" gap="1" ref={ref} style={style}>
        {children}
      </Flex>
    );
  }
);

CountryPickerList.displayName = 'CountryPickerList';

const virtuosoComponents: VirtuosoProps<Country, unknown>['components'] = {
  List: CountryPickerList,
};

export const CountryPicker = memo(function CountryPicker(props: IProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const results = useMemo(() => {
    const q = query.trim();

    if (!q) {
      return ORDERED_COUNTRIES;
    }

    return ORDERED_COUNTRIES.filter((c) => matchesQuery(c, q));
  }, [query]);

  const selectedIndex = results.findIndex((c) => c.code === props.country.code);

  const select = (next: Country) => {
    props.onChange(next);
    setOpen(false);
    setQuery('');
  };

  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);

        if (!next) {
          setQuery('');
        }
      }}
    >
      <Popover.Trigger>
        <Button
          variant="surface"
          color={RADIX.COLOR.NEUTRAL}
          disabled={props.disabled}
          aria-label={`Country: ${props.country.name} +${props.country.callingCode}`}
          style={{ flex: '0 0 auto' }}
        >
          <Flex align="center" gap="1">
            <span>{props.country.flag}</span>
            <span>+{props.country.callingCode}</span>
          </Flex>
        </Button>
      </Popover.Trigger>

      <Popover.Content align="start" sideOffset={4} width="320px">
        <Flex direction="column" gap="2">
          <TextField.Root
            size="2"
            placeholder="Search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && results[0]) {
                e.preventDefault();
                select(results[0]);
              }
            }}
          />

          {results.length === 0 ? (
            <Text size="2" color={RADIX.COLOR.NEUTRAL} align="center">
              No matches
            </Text>
          ) : (
            <Virtuoso
              style={{ height: LIST_HEIGHT }}
              data={results}
              components={virtuosoComponents}
              computeItemKey={(_, c) => c.code}
              initialTopMostItemIndex={
                selectedIndex > 0 && !query ? selectedIndex : 0
              }
              itemContent={(_, c) => {
                const isActive = c.code === props.country.code;
                return (
                  <Button
                    variant="soft"
                    color={isActive ? RADIX.COLOR.ACCENT : RADIX.COLOR.NEUTRAL}
                    className={`CountryPickerOption ${isActive ? 'active' : ''}`}
                    style={{ width: '100%' }}
                    onClick={() => select(c)}
                  >
                    <Flex align="center" gap="2" width="100%">
                      <Box>{c.flag}</Box>
                      <Text size="2">{c.name}</Text>
                      <Box ml="auto">
                        <Text size="2">+{c.callingCode}</Text>
                      </Box>
                    </Flex>
                  </Button>
                );
              }}
            />
          )}
        </Flex>
      </Popover.Content>
    </Popover.Root>
  );
});
